import type {
  AdvancedSearchState,
  QuerySyntaxSearch,
  SearchFieldConfig,
  StructuredSearch,
  StructuredSearchState
} from '../types'

export function createDefaultStructured(fields: SearchFieldConfig[]): StructuredSearchState {
  return {
    query: '',
    selectedFields: fields.filter((f) => f.defaultSelected).map((f) => f.fieldPath),
    matchType: 'best_fields',
    fuzzy: false,
    fuzzyEdits: 'auto',
    fuzzyPrefixLength: 0,
    booleanAnd: false,
    boost: 1,
    dateRange: { from: '', to: '' },
    fieldExists: []
  }
}

export function createDefaultState(fields: SearchFieldConfig[]): StructuredSearch {
  return {
    mode: 'structured',
    name: '',
    structured: createDefaultStructured(fields)
  }
}

export function createQuerySyntaxState(raw: string, name = ''): QuerySyntaxSearch {
  return { mode: 'querySyntax', name, querySyntax: { raw } }
}

export function isActiveState(state: AdvancedSearchState): boolean {
  if (state.mode === 'querySyntax') {
    return state.querySyntax.raw.trim().length > 0
  }

  const s = state.structured
  return s.query.trim().length > 0
    || s.dateRange.from.length > 0
    || s.dateRange.to.length > 0
    || s.fieldExists.length > 0
}

/**
 * Validates that a parsed JSON value has the minimum shape of AdvancedSearchState,
 * then merges with defaults so missing fields get safe values.
 * Use at deserialization boundaries (JSON.parse, URL params) to avoid unsafe `as` casts.
 *
 * Accepts the older shape that carried both modes at once by keeping whichever one
 * `mode` names, so searches saved before the split was removed still open.
 */
export function parseAdvancedSearchState(value: unknown, fields?: SearchFieldConfig[]): AdvancedSearchState | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const obj = value as Record<string, unknown>
  const name = typeof obj.name === 'string' ? obj.name : ''

  if (obj.mode === 'querySyntax') {
    const qs = obj.querySyntax
    if (typeof qs !== 'object' || qs === null) return undefined
    const raw = (qs as Record<string, unknown>).raw
    if (typeof raw !== 'string') return undefined
    return createQuerySyntaxState(raw, name)
  }

  if (obj.mode === 'structured') {
    const structured = obj.structured
    if (typeof structured !== 'object' || structured === null) return undefined
    if (typeof (structured as Record<string, unknown>).query !== 'string') return undefined
    return {
      mode: 'structured',
      name,
      structured: {
        ...createDefaultStructured(fields ?? []),
        ...structured as Partial<StructuredSearchState>
      }
    }
  }

  return undefined
}

/**
 * Parse a JSON string into a validated AdvancedSearchState.
 * Combines JSON.parse with shape validation; logs on failure.
 */
export function parseAdvancedSearchJson(value: string, context?: string, fields?: SearchFieldConfig[]): AdvancedSearchState | undefined {
  let parsed: unknown
  try {
    parsed = JSON.parse(value)
  } catch (err) {
    console.error(`[${context ?? 'parseAdvancedSearchJson'}] Failed to parse JSON:`, err)
    return undefined
  }
  return parseAdvancedSearchState(parsed, fields)
}
